import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  seoConfig,
  getPageSEO,
  getCanonicalUrl,
  generateOrganizationStructuredData,
  generateBreadcrumbStructuredData,
  generateArticleStructuredData,
  generateWebSiteStructuredData,
  generateSoftwareApplicationStructuredData,
  generatePageStructuredData,
} from "@/config/seo.config";

/**
 * SEO Component
 * Manages all meta tags, Open Graph, Twitter Cards, and structured data
 *
 * @param {string} page - Page key from seo.config.js (e.g., 'home', 'about')
 * @param {string} title - Custom title (overrides default)
 * @param {string} description - Custom description (overrides default)
 * @param {string} image - Custom image URL (overrides default)
 * @param {string} imageAlt - Alt text for the image
 * @param {string} keywords - Custom keywords (overrides default)
 * @param {array} breadcrumbs - Breadcrumb items [{name, url}]
 * @param {object} article - Article data for Article schema
 * @param {object} structuredData - Additional custom structured data
 * @param {string} type - Open Graph type (website, article, etc.)
 * @param {boolean} noindex - Disable search engine indexing
 * @param {string} canonical - Custom canonical URL
 * @param {string} locale - Language locale
 */
export function SEO({
  page = "home",
  title = null,
  description = null,
  image = null,
  imageAlt = null,
  keywords = null,
  breadcrumbs = null,
  article = null,
  structuredData = null,
  type = "website",
  noindex = false,
  canonical = null,
  locale = "en_US",
}) {
  const location = useLocation();
  const pageSEO = getPageSEO(page);

  // Use custom values or fall back to page defaults
  const finalTitle = title || pageSEO.title;
  const finalDescription = description || pageSEO.description;
  const finalImage = image || pageSEO.image;
  const finalImageAlt = imageAlt || pageSEO.imageAlt || finalTitle;
  const finalKeywords = keywords || pageSEO.keywords;
  const finalCanonical = canonical || getCanonicalUrl(location.pathname);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const setSingleLink = (selector, attrs) => {
      const links = Array.from(document.head.querySelectorAll(selector));
      let el = links[0] || null;

      if (!el) {
        el = document.createElement("link");
        document.head.appendChild(el);
      }

      Object.entries(attrs).forEach(([key, value]) => {
        el.setAttribute(key, value);
      });

      for (let i = 1; i < links.length; i += 1) {
        links[i].remove();
      }
    };

    const setSingleMeta = (selector, attrs) => {
      const metas = Array.from(document.head.querySelectorAll(selector));
      let el = metas[0] || null;

      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }

      Object.entries(attrs).forEach(([key, value]) => {
        el.setAttribute(key, value);
      });

      for (let i = 1; i < metas.length; i += 1) {
        metas[i].remove();
      }
    };

    setSingleLink('link[rel="canonical"]', {
      rel: "canonical",
      href: finalCanonical,
    });

    setSingleMeta('meta[name="title"]', {
      name: "title",
      content: finalTitle,
    });

    setSingleMeta('meta[name="description"]', {
      name: "description",
      content: finalDescription,
    });

    setSingleMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow",
    });

    setSingleMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content: noindex ? "noindex, nofollow" : "index, follow",
    });

    setSingleMeta('meta[property="og:url"]', {
      property: "og:url",
      content: finalCanonical,
    });

    setSingleMeta('meta[property="og:title"]', {
      property: "og:title",
      content: finalTitle,
    });

    setSingleMeta('meta[property="og:description"]', {
      property: "og:description",
      content: finalDescription,
    });

    setSingleMeta('meta[name="twitter:url"]', {
      name: "twitter:url",
      content: finalCanonical,
    });

    setSingleMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: finalTitle,
    });

    setSingleMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: finalDescription,
    });
  }, [finalCanonical, finalTitle, finalDescription, noindex]);

  // Generate structured data
  const schemas = [];

  // Always include Organization schema
  schemas.push(generateOrganizationStructuredData());

  // Include WebSite schema on homepage
  if (page === "home") {
    schemas.push(generateWebSiteStructuredData());
    schemas.push(generateSoftwareApplicationStructuredData());
  }

  // Include a page-level schema for all pages (AboutPage, ContactPage, CollectionPage, etc.)
  schemas.push(
    generatePageStructuredData(page, location.pathname, {
      title: finalTitle,
      description: finalDescription,
      image: finalImage,
    })
  );

  // Include Article schema if article data provided
  if (article) {
    schemas.push(generateArticleStructuredData(article));
  }

  // Include Breadcrumb schema if breadcrumbs provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbStructuredData(breadcrumbs));
  }

  // Include custom structured data
  if (structuredData) {
    schemas.push(structuredData);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}

      {/* Robots */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:image:type" content="image/webp" />
      <meta property="og:image:alt" content={finalImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={pageSEO.siteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalCanonical} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={finalImageAlt} />
      <meta name="twitter:site" content={seoConfig.site.twitter} />

      {/* Article Specific Meta Tags */}
      {article && (
        <>
          <meta
            property="article:published_time"
            content={article.created_at || article.publishedAt}
          />
          <meta
            property="article:modified_time"
            content={article.updated_at || article.modifiedAt}
          />
          {article.author_name && <meta property="article:author" content={article.author_name} />}
          {article.category_name && (
            <meta property="article:section" content={article.category_name} />
          )}
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
